import React, { useMemo, useState } from 'react';
import { useNavigation } from '../contexts/NavigationProvider';
import { useAuth } from '../contexts/AuthProvider';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import MapPinIcon from './icons/MapPinIcon';
import HeartIcon from './icons/HeartIcon';
import type { User } from '../types';

const ALL = 'all';

const FanListPage: React.FC = () => {
    const { viewingFanListOwner, handleNavigation, handleViewProfile, navigationOrigin } = useNavigation();
    const { users, currentUser } = useAuth();
    const [selectedRegion, setSelectedRegion] = useState(ALL);
    const [selectedCity, setSelectedCity] = useState(ALL);

    const userMap = useMemo(() => new Map(users.map(u => [u.handle, u])), [users]);

    const fans = useMemo(() => {
        if (!viewingFanListOwner) return [];
        return (viewingFanListOwner.fans || [])
            .map(h => userMap.get(h))
            .filter((u): u is User => !!u);
    }, [viewingFanListOwner, userMap]);

    const isOwner = useMemo(() => {
        if (!currentUser || !viewingFanListOwner) return false;
        if ('handle' in viewingFanListOwner) return viewingFanListOwner.handle === currentUser.handle;
        if ('members' in viewingFanListOwner) return viewingFanListOwner.members.some(m => m.handle === currentUser.handle);
        return false;
    }, [currentUser, viewingFanListOwner]);

    const regions = useMemo(() => {
        const set = new Set<string>();
        fans.forEach(f => { if (f.region) set.add(f.region); });
        return Array.from(set).sort();
    }, [fans]);

    const cities = useMemo(() => {
        const set = new Set<string>();
        fans.forEach(f => {
            if (!f.city) return;
            if (selectedRegion === ALL || f.region === selectedRegion) set.add(f.city);
        });
        return Array.from(set).sort();
    }, [fans, selectedRegion]);

    const filteredFans = useMemo(() => {
        return fans.filter(f => {
            if (selectedRegion !== ALL && f.region !== selectedRegion) return false;
            if (selectedCity !== ALL && f.city !== selectedCity) return false;
            return true;
        });
    }, [fans, selectedRegion, selectedCity]);

    const geoRanking = useMemo(() => {
        const counts: Record<string, number> = {};
        fans.forEach(fan => {
            const area = fan.city
                ? `${fan.city}${fan.region ? ', ' + fan.region : ''}${fan.country ? ', ' + fan.country : ''}`
                : (fan.country || 'Global');
            counts[area] = (counts[area] || 0) + 1;
        });
        return Object.entries(counts).sort((a, b) => b[1] - a[1]);
    }, [fans]);

    const handleRegionChange = (val: string) => {
        setSelectedRegion(val);
        setSelectedCity(ALL);
    };

    if (!viewingFanListOwner) return null;

    const ownerName = viewingFanListOwner.name;
    const selectClass = "bg-gray-800 text-white text-xs font-bold rounded-lg px-3 py-2 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 cursor-pointer";

    return (
        <div className="space-y-6 font-urbanist animate-in fade-in duration-500">
            <button onClick={() => handleNavigation(navigationOrigin || 'feed')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold transition-colors">
                <ArrowLeftIcon className="h-5 w-5" />
                <span>Back</span>
            </button>

            {/* Header */}
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                <div className="flex items-center space-x-4 mb-6">
                    <div className="bg-pink-500/10 p-3 rounded-xl">
                        <HeartIcon className="h-8 w-8 text-pink-500 fill-current" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-extrabold text-white tracking-tighter uppercase">Fans of {ownerName}</h2>
                        <p className="text-brand-muted text-sm uppercase font-bold tracking-widest">{fans.length} registered stage-side supporters</p>
                    </div>
                </div>

                {/* Filters */}
                {fans.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3">
                        <MapPinIcon className="h-4 w-4 text-pink-500 flex-shrink-0" />
                        <select value={selectedRegion} onChange={e => handleRegionChange(e.target.value)} className={selectClass}>
                            <option value={ALL}>All Regions</option>
                            {regions.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <select value={selectedCity} onChange={e => setSelectedCity(e.target.value)} className={selectClass} disabled={cities.length === 0}>
                            <option value={ALL}>All Cities</option>
                            {cities.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {(selectedRegion !== ALL || selectedCity !== ALL) && (
                            <button
                                onClick={() => { setSelectedRegion(ALL); setSelectedCity(ALL); }}
                                className="text-xs text-pink-400 hover:text-white font-bold transition-colors"
                            >
                                Clear filters
                            </button>
                        )}
                        <span className="text-xs text-brand-muted font-mono ml-auto">
                            {filteredFans.length} / {fans.length} fans
                        </span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Fan grid */}
                <div className="lg:col-span-2">
                    {filteredFans.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredFans.map(fan => {
                                const fanLoc = [fan.city, fan.region, fan.country].filter(Boolean).join(', ') || 'Global';
                                return (
                                    <div
                                        key={fan.handle}
                                        onClick={() => { if (isOwner) handleViewProfile(fan); }}
                                        className={`bg-brand-surface p-4 rounded-xl border border-gray-800 transition-all group flex items-center gap-4 ${isOwner ? 'hover:border-pink-500/50 cursor-pointer' : 'cursor-default'}`}
                                    >
                                        <img src={fan.avatarUrl} className={`h-16 w-16 rounded-full border-2 border-gray-700 transition-colors ${isOwner ? 'group-hover:border-pink-500/50' : ''}`} alt="" />
                                        <div className="flex-1 min-w-0">
                                            <p className={`font-black text-white text-lg truncate tracking-tight transition-colors ${isOwner ? 'group-hover:text-pink-400' : ''}`}>{fan.name}</p>
                                            <p className="text-xs text-brand-muted font-mono">@{fan.handle}</p>
                                            <div className="flex items-center text-brand-muted mt-2">
                                                <MapPinIcon className="h-3.5 w-3.5 mr-1 text-pink-500 flex-shrink-0" />
                                                <span className="text-[10px] font-black tracking-wider truncate">{fanLoc}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-brand-surface rounded-xl p-16 text-center border-2 border-dashed border-gray-800">
                            <MapPinIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
                            <p className="text-white font-bold mb-1">No fans in this area</p>
                            <p className="text-brand-muted text-xs font-bold tracking-widest uppercase">Try a different filter</p>
                        </div>
                    )}

                    {!isOwner && fans.length > 0 && (
                        <p className="text-center text-[10px] text-brand-muted uppercase font-black tracking-[0.3em] py-8">
                            Subscriber profiles are hidden to respect audience privacy.
                        </p>
                    )}
                </div>

                {/* Geo ranking sidebar */}
                {geoRanking.length > 0 && (
                    <div className="lg:col-span-1">
                        <div className="bg-brand-surface rounded-xl border border-gray-800 p-5 sticky top-4">
                            <h4 className="text-[10px] font-black text-brand-orange uppercase tracking-[0.2em] mb-4">Audience Hotspots</h4>
                            <div className="space-y-2">
                                {geoRanking.map(([area, count], i) => {
                                    const pct = Math.round((count / fans.length) * 100);
                                    return (
                                        <div key={area} className="space-y-1">
                                            <div className="flex justify-between items-center text-xs font-bold">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-brand-muted font-mono w-4 flex-shrink-0">{i + 1}</span>
                                                    <span className="text-gray-300 truncate">{area}</span>
                                                </div>
                                                <span className="text-white font-mono ml-2 flex-shrink-0">{count}</span>
                                            </div>
                                            <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-pink-500/60 rounded-full transition-all"
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FanListPage;
